import { Injectable } from '@angular/core';
import {
  PostJourneysGetJourneyResponseDataFlightsInner,
  PostJourneysGetJourneyResponseDataTravelersInnerRegulatoryDetails,
} from 'src/sdk_checkin';
import {
  ImmigrationDocument,
  ImmigrationInformation,
  DestinationAddress,
} from '../../interface/checkin-passenger-input';

@Injectable({
  providedIn: 'root',
})
export class CheckinPassengerInputImmigrationService {
  /**
   * 入国書類情報を取得する
   * @param flights セグメント情報
   * @param regulatoryDetails 入国関連情報オブジェクト
   * @param hasFlightToUs 入アメリカ着のセグメントが存在するか否か
   * @returns 入国書類情報リスト
   */
  getImmigrations(
    flights: PostJourneysGetJourneyResponseDataFlightsInner[],
    regulatoryDetails?: PostJourneysGetJourneyResponseDataTravelersInnerRegulatoryDetails,
    hasFlightToUs?: boolean
  ): ImmigrationInformation[] {
    const immigrations: ImmigrationInformation[] = [];
    const arrivalCountryCodes = this.getArrivalCountryCodes(flights);

    const lastFlightCountryCode = flights?.[flights.length - 1]?.arrival.countryCode;
    arrivalCountryCodes.forEach((arrivalCountryCode) => {
      var visas: ImmigrationDocument[] | undefined;
      if (arrivalCountryCode === 'US' || (!hasFlightToUs && arrivalCountryCode === lastFlightCountryCode)) {
        visas = this.getVisas(arrivalCountryCode, regulatoryDetails, true);
      } else {
        visas = this.getVisas(arrivalCountryCode, regulatoryDetails, false);
      }
      const greenCards = this.getGreenCards(arrivalCountryCode, regulatoryDetails);
      const destinationAddresses = this.getDestinationAddresses(arrivalCountryCode, regulatoryDetails);

      immigrations.push({
        countryCode: arrivalCountryCode,
        selectDocument: 'N',
        isTransit: arrivalCountryCode === 'US' && !!destinationAddresses?.every((address) => address.isTransit),
        visas: visas,
        greenCards: greenCards,
        destinationAddresses: destinationAddresses,
      });
    });

    immigrations.forEach((immigration) => {
      immigration.selectDocument = this.getSelectDocument(immigration.visas, immigration.greenCards);
    });

    return immigrations;
  }

  /**
   * 入国リスト取得する
   * @param flights セグメント情報
   * @returns 入国リスト
   */
  private getArrivalCountryCodes(flights: PostJourneysGetJourneyResponseDataFlightsInner[]): (string | undefined)[] {
    const countryCodes = flights
      .filter((flight) => flight.departure.countryCode !== flight.arrival.countryCode)
      .map((flight) => flight.arrival.countryCode);

    return this.removeDuplicateCountryCodes(countryCodes);
  }

  /**
   * 入国リストで重複する国を削除する
   * @param countryCodes 国コードリスト
   * @returns 重複なしの入国リスト
   */
  private removeDuplicateCountryCodes(countryCodes: (string | undefined)[]): (string | undefined)[] {
    const codes: (string | undefined)[] = [];

    countryCodes.forEach((countryCode) => {
      if (!codes.includes(countryCode)) {
        codes.push(countryCode);
      }
    });

    return codes;
  }

  /**
   * ビザ情報を取得する
   * @param countryCode 国コード
   * @param regulatoryDetails 入国関連情報オブジェクト
   * @param isIncludeEmptyNationality 抽出条件に適用国が存在しないものを含むかどうか
   * @returns ビザ情報リスト
   */
  private getVisas(
    countryCode?: string,
    regulatoryDetails?: PostJourneysGetJourneyResponseDataTravelersInnerRegulatoryDetails,
    isIncludeEmptyNationality?: boolean
  ): ImmigrationDocument[] | undefined {
    const checkNationarityCode = isIncludeEmptyNationality
      ? (nationalityCode?: string, countryCode?: string) => nationalityCode === countryCode || !nationalityCode
      : (nationalityCode?: string, countryCode?: string) => nationalityCode === countryCode;

    const visas = regulatoryDetails?.visas
      ?.filter((visa) => checkNationarityCode(visa.nationalityCode, countryCode))
      .map((visa) => {
        return {
          number: visa.number,
          expiryDate: visa.expiryDate,
          isNoExpiration: visa.isNoExpiration,
        };
      });
    return visas?.length ? visas : undefined;
  }

  /**
   * グリーンカード情報を取得する
   * @param countryCode 国コード
   * @param regulatoryDetails 入国関連情報オブジェクト
   * @returns グリーンカード情報リスト
   */
  private getGreenCards(
    countryCode?: string,
    regulatoryDetails?: PostJourneysGetJourneyResponseDataTravelersInnerRegulatoryDetails
  ): ImmigrationDocument[] | undefined {
    let greenCards: ImmigrationDocument[] | undefined;

    if (countryCode === 'US') {
      greenCards = regulatoryDetails?.greenCards?.map((greenCard) => {
        return {
          number: greenCard.number,
          expiryDate: greenCard.expiryDate,
          isNoExpiration: greenCard.isNoExpiration,
        };
      });
    }

    return greenCards;
  }

  /**
   * 滞在先情報を取得する
   * @param countryCode 国コード
   * @param regulatoryDetails 入国関連情報オブジェクト
   * @returns 滞在先情報リスト
   */
  private getDestinationAddresses(
    countryCode?: string,
    regulatoryDetails?: PostJourneysGetJourneyResponseDataTravelersInnerRegulatoryDetails
  ): DestinationAddress[] | undefined {
    let destinationAddresses: DestinationAddress[] | undefined;

    if (countryCode === 'US') {
      destinationAddresses = regulatoryDetails?.destinationAddresses
        ?.filter((destinationAddress) => destinationAddress.countryCode === countryCode)
        .map((destinationAddress) => {
          return {
            lines: destinationAddress.lines ? destinationAddress.lines : '',
            zipCode: destinationAddress.zipCode ? destinationAddress.zipCode : '',
            cityName: destinationAddress.cityName ? destinationAddress.cityName : '',
            stateCode: destinationAddress.stateCode ? destinationAddress.stateCode : '',
            isTransit: destinationAddress.isTransit ? destinationAddress.isTransit : false,
          };
        });
    }

    return destinationAddresses?.length ? destinationAddresses : undefined;
  }

  /**
   * 入国情報選択値を取得する
   * @param visas ビザ情報リスト
   * @param greenCards グリーンカード情報リスト
   * @returns 入国情報選択値
   */
  private getSelectDocument(visas?: ImmigrationDocument[], greenCards?: ImmigrationDocument[]): string {
    let initial = 'N';

    if (greenCards?.length) {
      initial = 'C';
    } else if (visas?.length) {
      initial = 'V';
    }

    return initial;
  }
}
